import { useEffect, useState } from 'react';

export function getSlideImageUrl(slide: SpApi.ISlideshowSlide): string | null {
    const firstItem = slide.items.find((item) => item.displayUrl !== null);

    return (
        firstItem?.displayUrl?.extraLarge3 ??
        firstItem?.displayUrl?.extraLarge2 ??
        firstItem?.displayUrl?.large ??
        firstItem?.displayUrl?.medium ??
        null
    );
}

/**
 * Preloads every slide's image into the browser cache ahead of playback, so a
 * slide's asset is already available by the time its transition begins — without
 * this, the incoming slide's <img> only starts fetching once it enters the DOM
 * (i.e. when the transition itself starts), which pops in mid-animation on a cold
 * cache (typically the first playthrough after a hard refresh).
 *
 * A slide with no image, or one whose image fails to load, is still marked ready
 * so a single broken asset can't stall playback indefinitely.
 */
export function useSlideshowImagePreload(slides: SpApi.ISlideshowSlide[]): ReadonlySet<number> {
    const [readyIndices, setReadyIndices] = useState<ReadonlySet<number>>(new Set());

    useEffect(() => {
        setReadyIndices(new Set());

        let cancelled = false;
        const images: HTMLImageElement[] = [];

        const markReady = (index: number) => {
            if (cancelled) {
                return;
            }

            setReadyIndices((prev) => {
                if (prev.has(index)) {
                    return prev;
                }

                const next = new Set(prev);
                next.add(index);
                return next;
            });
        };

        slides.forEach((slide, index) => {
            const url = getSlideImageUrl(slide);

            if (!url) {
                markReady(index);
                return;
            }

            const img = new Image();

            images.push(img);
            img.onload = () => markReady(index);
            img.onerror = () => markReady(index);
            img.src = url;

            if (img.complete) {
                markReady(index);
            }
        });

        return () => {
            cancelled = true;
            images.forEach((img) => {
                img.onload = null;
                img.onerror = null;
            });
        };
    }, [slides]);

    return readyIndices;
}
